/**
 * COMP-07 — pure companion-overlay helpers for the Club tab.
 *
 * The Club tab overlays the user's OWN HR trace against ONE companion's ("Dad's
 * watch") on a single ECharts chart with a swimmer switcher. This module is the
 * pure, testable seam behind that view:
 *
 *   - `loadCompanions()` wraps `bridge.listCompanions` (stub-safe) to fetch club
 *     member summaries. A member with `workoutCount === 0` is a VIEWER — a
 *     first-class member, not an empty-state error.
 *   - `buildOverlaySeries(userFh, companionFh, label)` builds the TWO-trace HR
 *     overlay via the canonical `buildHrSeries` (NEVER re-implementing the HR
 *     series shape). Two series, not one — the user's and the companion's. Each
 *     series is rebased to elapsed seconds from its own `workout.startDate`
 *     (council §1.1) so date-disjoint swims share one axis honestly; the
 *     canonical absolute-time series in hrSeries.ts stays wall-clock.
 *   - `classifyCompanion(summary)` reuses the Phase-5 `CURRENT_SCHEMA_VERSION`
 *     gate (NEVER a hand-rolled compare): a NEWER-than-this-app envelope is
 *     `"unsupported-schema"` (the tab renders a VISIBLE rejection); the current
 *     version and any OLDER/legacy version (incl. v0) are `"ok"` and render —
 *     mirroring parseImport's v0 tolerance so Dad's real older-watch file is
 *     NOT rejected.
 */
import type {
  CompanionSummary,
  FirehoseSampleEntry,
  ListCompanionsResponse,
  SwimFirehoseResponse,
} from "@contract/types";
import { bridge } from "../bridge/client";
import { buildHrSeries, type HrPoint } from "./hrSeries";
import { CURRENT_SCHEMA_VERSION } from "./share";

/** The HKQuantityType bucket name HR samples live under in the firehose. */
const HR_TYPE = "HKQuantityTypeIdentifierHeartRate";

/** One named HR trace ready to feed an ECharts series. */
export interface OverlaySeries {
  name: string;
  /** HR points with null rows dropped — used for display when grid is not needed. */
  points: HrPoint[];
  /**
   * The full resampled [elapsed, bpm|null][] grid — every entry aligned to the
   * shared x-axis. Null entries mark rest gaps (line breaks). The tooltip
   * formatter uses this to look up both swimmers' values at the same x.
   */
  _grid?: [number, number | null][];
}

/** Result of the envelope-version gate for a club member. */
export type CompanionClassification = "ok" | "unsupported-schema";

/**
 * Load the club's member summaries from the native bridge (stub-safe via the
 * client). A viewer (workoutCount 0) appears in this list and is NOT filtered —
 * the tab decides how to render a zero-record member.
 */
export async function loadCompanions(): Promise<ListCompanionsResponse> {
  return bridge.listCompanions();
}

/**
 * Pull the HR samples out of a firehose (or an absent firehose) and build the
 * canonical `{ ts, bpm }[]` series. A null/empty firehose yields an empty-but-
 * valid series — a zero-record viewer never crashes the overlay.
 */
function hrPointsFrom(fh: SwimFirehoseResponse | null): HrPoint[] {
  if (!fh) return [];
  const bucket = fh.buckets.find((b) => b.typeName === HR_TYPE);
  const samples: FirehoseSampleEntry[] = bucket?.samples ?? [];
  return buildHrSeries(samples);
}

/**
 * Like `hrPointsFrom`, but rebased to ELAPSED SECONDS from this swim's own
 * `workout.startDate` — so two swims recorded on different days share one axis
 * honestly instead of stretching an absolute-time axis across the day gap
 * (council §1.1). The canonical absolute-time series lives in hrSeries.ts and is
 * UNTOUCHED — sets/rest/phases consumers still need wall-clock. A null/empty
 * firehose has no start to subtract, so it yields `[]` (mirroring `hrPointsFrom`
 * — a zero-record viewer renders an honest empty trace, never throws).
 */
function elapsedPointsFrom(fh: SwimFirehoseResponse | null): HrPoint[] {
  if (!fh) return [];
  const start = Date.parse(fh.workout.startDate);
  return hrPointsFrom(fh).map((p) => ({
    ts: (p.ts - start) / 1000,
    bpm: p.bpm,
  }));
}

/** Bucket size in seconds for the shared elapsed grid. */
const RESAMPLE_BUCKET_S = 5;

/**
 * Resample a sparse elapsed-seconds HR trace onto a uniform grid of `step`-
 * second buckets. Each bucket takes the nearest original sample within the
 * window — i.e. the sample whose elapsed ts is closest to the bucket centre.
 * Buckets with no sample within the window remain `null` so rest gaps still
 * break the line. The resulting array is keyed to the same x[] grid returned
 * by `buildSharedGrid`.
 *
 * @param points  Elapsed-seconds HR trace (from `elapsedPointsFrom`).
 * @param xGrid   The shared bucket centres to sample onto.
 * @returns       Parallel array of [elapsed, bpm | null] pairs — ECharts
 *                `data` entries that respect null for line breaks.
 */
function resampleOntoGrid(
  points: HrPoint[],
  xGrid: number[],
): [number, number | null][] {
  if (points.length === 0) return xGrid.map((x) => [x, null]);

  return xGrid.map((x) => {
    // Find the nearest point within half a bucket-width (2.5 s default).
    const half = RESAMPLE_BUCKET_S / 2;
    let bestDist = Infinity;
    let bestBpm: number | null = null;
    for (const p of points) {
      const d = Math.abs(p.ts - x);
      if (d <= half && d < bestDist) {
        bestDist = d;
        bestBpm = p.bpm;
      }
    }
    return [x, bestBpm];
  });
}

/**
 * Build a shared elapsed-seconds grid that spans both traces. Buckets are
 * `RESAMPLE_BUCKET_S`-second wide; the grid starts at 0 and ends at the max
 * elapsed ts of either trace (rounded up to the next bucket).
 */
function buildSharedGrid(userPts: HrPoint[], companionPts: HrPoint[]): number[] {
  const allTs = [...userPts.map((p) => p.ts), ...companionPts.map((p) => p.ts)];
  if (allTs.length === 0) return [];
  const maxTs = Math.max(...allTs);
  const buckets: number[] = [];
  for (let t = 0; t <= maxTs + RESAMPLE_BUCKET_S; t += RESAMPLE_BUCKET_S) {
    buckets.push(t);
  }
  return buckets;
}

/**
 * Build the TWO-trace HR overlay: the user's OWN trace (from `userFh`, fetched
 * by the caller via `bridge.getWorkoutFirehose`) plus the companion's trace
 * (from `companionFh`, via `bridge.getCompanionFirehose`), each as a labeled
 * series. Returns EXACTLY TWO series — the overlay is two traces, not one.
 *
 * Both traces are RESAMPLED onto a shared elapsed-seconds grid (5-second
 * buckets) so every axis snap has a point from each series, enabling the tooltip
 * to always show BOTH swimmers at the same moment. Null values preserve rest gaps
 * (line breaks) for buckets where neither swimmer has a nearby sample.
 *
 * Each series is rebased to elapsed seconds from its OWN `workout.startDate`
 * (council §1.1) before resampling, so date-disjoint swims share one axis
 * honestly. The canonical absolute-time series (hrSeries.ts) is untouched —
 * sets/rest/phases consumers still read wall-clock. A null or zero-record
 * companion firehose yields a null-filled companion series so a viewer member
 * renders an honest empty trace rather than throwing.
 */
export function buildOverlaySeries(
  userFh: SwimFirehoseResponse | null,
  companionFh: SwimFirehoseResponse | null,
  companionLabel: string,
): [OverlaySeries, OverlaySeries] {
  const userPts = elapsedPointsFrom(userFh);
  const companionPts = elapsedPointsFrom(companionFh);

  // Build a shared grid so both series snap to the same x-values.
  const xGrid = buildSharedGrid(userPts, companionPts);

  // Resample each trace onto the shared grid. Empty traces get all-null grids.
  const userResampled = resampleOntoGrid(userPts, xGrid);
  const companionResampled = resampleOntoGrid(companionPts, xGrid);

  // Convert [elapsed, bpm|null] pairs back to HrPoint[] (null rows become ts
  // entries with bpm=0 won't work for null breaks — use a different shape).
  // The ECharts series.data accepts [x, y|null], so we expose that directly on
  // the points array: ts = elapsed, bpm = value (NaN when null to allow
  // downstream null-checks, but callers should use the raw resampled array).
  // We attach the raw grid for callers that need it (tooltip nearest-value
  // lookup), and keep OverlaySeries.points for the existing display path.

  // Map resampled [x, v|null] to HrPoint[], dropping null rows (line breaks
  // are handled in the ECharts data array instead, not in HrPoint[]).
  const toPoints = (rows: [number, number | null][]): HrPoint[] =>
    rows.filter((r): r is [number, number] => r[1] !== null).map(([ts, bpm]) => ({ ts, bpm }));

  return [
    { name: "You", points: toPoints(userResampled), _grid: userResampled },
    { name: companionLabel, points: toPoints(companionResampled), _grid: companionResampled },
  ];
}

/**
 * Classify a club member's envelope version against the Phase-5 gate. Returns
 * `"unsupported-schema"` ONLY when the member's `schemaVersion` EXCEEDS
 * `CURRENT_SCHEMA_VERSION` (a too-NEW envelope from a newer app build) — the
 * tab renders a visible rejection, never a silent drop. The current version AND
 * any OLDER/legacy version (`< CURRENT`, incl. v0) classify `"ok"` and render,
 * mirroring parseImport's v0 tolerance (Dad's older-watch file must NOT be
 * rejected). Reuses the share.ts constant — no hand-rolled version compare.
 */
export function classifyCompanion(
  summary: CompanionSummary,
): CompanionClassification {
  return summary.schemaVersion > CURRENT_SCHEMA_VERSION
    ? "unsupported-schema"
    : "ok";
}
